
import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BehaviorSubject, filter, Observable, Subscription } from 'rxjs';
import { ATEquation, EquationData } from './equation.models';
import { ChatbotConfigService } from '../../chatbot-core/chatbot-config/chatbot-config.service';

/** input editor for equation */
@Component({
    selector: 'app-equation-editor',
    imports: [FormsModule],
    templateUrl: './equation-editor.component.html',
    styleUrls: ['./equation-editor.component.scss']
})
export class EquationEditorComponent implements OnInit, OnChanges {
  @Input('equation') equation: ATEquation | null = null;
  @Input('readOnly') readOnly: boolean = false;

  @Output('onKeyDown') onKeyDown: EventEmitter<KeyboardEvent> = new EventEmitter();

  public equationEditorUrl: SafeResourceUrl | null = null;
  @ViewChild('equationEditor') equationEditor: any;
  public isEditorOpen: boolean = false;
  private _iframeKeyListener?: () => void;

  private _onEditorLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _onEditorLoaded: Observable<boolean> = this._onEditorLoadedSubject.asObservable().pipe(filter((r) => r));
  private _loadEditorSubscription: Subscription = new Subscription();
  private _isEquationLoaded: boolean = false;

  constructor(
    private _sanitizer: DomSanitizer,
    private _zone: NgZone,
    private _configService: ChatbotConfigService
  ) { }

  ngAfterViewInit() {
    // Listen for ESC on iframe document (if same origin)
    this.equationEditor.nativeElement.addEventListener('load', () => {
      try {
        const iframeDoc = this.equationEditor.nativeElement.contentDocument;
        if (iframeDoc) {
          this._iframeKeyListener = this.addKeyListener(iframeDoc);
        }
      } catch (error) {
        console.warn('Cannot access iframe document:', error);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['equation'] || changes['id']) {
      this.loadEquationInEditor();
    }
  }

  /**
   * listen to keys in @param doc and emit to subscribers
   * @param doc
   * @returns function to be alled to stop listening
   */
  private addKeyListener(doc: Document): () => void {
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.stopImmediatePropagation();  // stops ALL other listeners on the same element
        event.preventDefault();            // prevents browser default
        // Trigger the same action as clicking OK
        this._zone.run(() => {
          this.onKeyDown.next(event);      // still emit so parent can handle OK logic
        });
        return;
      }

      this._zone.run(() => {
        this.onKeyDown.next(event);
      });
    };

    // true = capture phase: fires BEFORE the equation editor's own internal handlers
    doc.addEventListener('keydown', listener, true);
    return () => doc.removeEventListener('keydown', listener, true);
  }

  ngOnInit(): void {
    const config = this._configService.getConfig();
    let url: string;

    if (config?.equationPlayerUrl && config.equationPlayerUrl.trim() !== '') {
      url = config.equationPlayerUrl;
    } else {
      console.log('equationPlayerUrl is not set. Using default equation editor URL');
      url = '../../assets/EquationEditorComponent/EquationEditor.html?iframeName=iframeEquationEditor';
    }

    this.equationEditorUrl = this._sanitizer.bypassSecurityTrustResourceUrl(url);
    this.loadEquationInEditor();
  }

  ngOnDestroy() {
    if (this._iframeKeyListener) {
      this._iframeKeyListener();
    }
  }

  /** get current equation */
  public async getEquation(): Promise<EquationData> {
    let equation: string = await this.equationEditor.nativeElement.contentWindow.M_GetEquationData(
      'iframeEquationEditor'
    );
    let isAnswered: boolean = this.equationEditor.nativeElement.contentWindow.IsEquationAnswered();

    let result: EquationData = {
      equation: equation,
      hasValue: isAnswered,
    };

    return result;
  }

  /** check if the editor and equation finished loading */
  public isLoaded(): boolean {
    return this._isEquationLoaded;
  }

  /** callback when the iframe finish loading */
  public onEquationEditorLoaded(): void {
    this._onEditorLoadedSubject.next(true);
  }

  /** load equation from input into editor */
  private loadEquationInEditor(): void {
    this._isEquationLoaded = false;

    this._loadEditorSubscription.unsubscribe();
    this._loadEditorSubscription = this._onEditorLoaded.subscribe((r) => {
      let equation = !!this.equation ? this.equation : null;
      const loadingOptions = this.equationEditor.nativeElement?.contentWindow?.getLoadingOptions();

      if (loadingOptions) {
        loadingOptions.hideSingleDelimiters = true;
        loadingOptions.hideStrikeThrough = true;
      }

      this.equationEditor.nativeElement?.contentWindow?.M_LoadEquation(
        equation?.equation,
        this.readOnly,
        loadingOptions,
        true
      );
      this._isEquationLoaded = true;
    });
  }
}
